'use client';

import { memo, useState, useCallback, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAccount } from 'wagmi';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { Spinner } from '@cbhq/cds-web/loaders/Spinner';
import { TwoColumnLayout } from '@/components/common/TwoColumnLayout';
import { DEFAULT_CARD_SPACER } from '@/constants/layout';
import { PortfolioPositions } from '@/components/Portfolio/PortfolioPositions';
import { PortfolioPositionsEmptyState } from '@/components/Portfolio/PortfolioPositionsEmptyState';
import { USDCRewardsCard } from '@/components/Portfolio/USDCRewardsCard';
import { TangorPortfolioHeader, PortfolioTab } from './TangorPortfolioHeader';
import { TangorPoolsOverview } from './TangorPoolsOverview';
import { TangorEquitiesOverview } from './TangorEquitiesOverview';
import { TangorEquitiesTabs } from './TangorEquitiesTabs';
import { TangorEquitiesEmptyState } from './TangorEquitiesEmptyState';
import { DividendsPayoutCard } from './DividendsPayoutCard';
import { UpcomingEventsCard } from './UpcomingEventsCard';
import { useTangorPortfolioData } from './hooks/useTangorPortfolioData';

// In the Tokenized Equities app there is no Pools tab — always open on equities.
const isTokenizedEquitiesApp = process.env.NEXT_PUBLIC_APP_MODE === 'tokenized-equities';

export const TangorPortfolioLayout = memo(function TangorPortfolioLayout() {
  const { address } = useAccount();
  const searchParams = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab: PortfolioTab =
    isTokenizedEquitiesApp || tabParam === 'equities' ? 'tokenized-equities' : 'pools';
  const [activeTab, setActiveTab] = useState<PortfolioTab>(initialTab);

  // All hooks must be called before any conditional returns
  const {
    showRewardsCard,
    // Equities data
    equityAssets,
    equityHistory,
    equitiesOverview,
    totalEquitiesValue,
    isEquityLoading,
  } = useTangorPortfolioData();

  const handleTabChange = useCallback((tab: PortfolioTab) => {
    setActiveTab(tab);
  }, []);

  // Sync with URL param changes.
  // In the Tokenized Equities app, always stay on the equities tab.
  useEffect(() => {
    if (tabParam === 'equities') {
      setActiveTab('tokenized-equities');
    } else if (tabParam === 'pools') {
      setActiveTab(isTokenizedEquitiesApp ? 'tokenized-equities' : 'pools');
    }
  }, [tabParam]);

  // If no wallet connected, show empty state
  if (!address) {
    return <PortfolioPositionsEmptyState />;
  }

  // Header component
  const header = <TangorPortfolioHeader activeTab={activeTab} onTabChange={handleTabChange} />;

  // Pools tab main content
  const poolsMainContent = (
    <VStack gap={DEFAULT_CARD_SPACER}>
      <Suspense fallback={null}>
        <TangorPoolsOverview />
      </Suspense>
      <PortfolioPositions />
    </VStack>
  );

  // Pools tab sidebar
  const poolsSidebar = showRewardsCard ? <USDCRewardsCard /> : undefined;

  const hasEquityAssets = equityAssets.length > 0;

  // Equities tab main content
  const equitiesMainContent = isEquityLoading ? (
    <HStack justifyContent="center" alignItems="center" paddingY={8}>
      <Spinner size={3} />
    </HStack>
  ) : hasEquityAssets ? (
    <VStack gap={DEFAULT_CARD_SPACER}>
      <TangorEquitiesOverview data={equitiesOverview} />
      <TangorEquitiesTabs
        assets={equityAssets}
        history={equityHistory}
        totalValue={totalEquitiesValue}
      />
    </VStack>
  ) : (
    <TangorEquitiesEmptyState />
  );

  // Equities tab sidebar (hidden when no equity assets)
  const equitiesSidebar = hasEquityAssets ? (
    <VStack gap={DEFAULT_CARD_SPACER}>
      <DividendsPayoutCard />
      <UpcomingEventsCard />
    </VStack>
  ) : undefined;

  return (
    <TwoColumnLayout
      header={header}
      main={activeTab === 'pools' ? poolsMainContent : equitiesMainContent}
      sidebar={activeTab === 'pools' ? poolsSidebar : equitiesSidebar}
    />
  );
});
